import EditButton from './EditButton'
import {FC, useEffect, useState} from 'react'
import {toAbsoluteUrl} from '_metronic/helpers'
import Card from './Card'
import {deleteObject, ref} from 'firebase/storage'
import db, {storage} from 'services/firebase'
import {useAuth} from 'app/modules/auth'
import AddEditPolicyModal from './modals/AddEditPolicyModal'
import {addDoc, collection, deleteDoc, doc, onSnapshot, serverTimestamp} from '@firebase/firestore'
import {ToastContainer} from 'react-toastify'
import Button from 'app/components/Button'
import {showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import CustomDrawer from './Drawer'

export type PolicyDataType = {
  id: string
  title: string
  documentURL: string | File
  documentName: string
  documentId?: string
  access: string
  policyId?: string
  isCustomDocumentUploaded?: boolean
}

type Props = {
  addedPolicies: PolicyDataType[]
}

const Policies: FC<Props> = ({addedPolicies}) => {
  const {currentUser} = useAuth()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [toEditPolicy, setToEditPolicy] = useState<PolicyDataType>({
    id: '',
    title: '',
    documentName: '',
    documentURL: '',
    access: '',
  })

  const [toDeletePolicy, setToDeletePolicy] = useState<PolicyDataType>()

  const [loading, setLoading] = useState(false)

  const [defaultPolicies, setDefaultPolicies] = useState<
    {
      id?: string
      title: string
      documentURL?: string
    }[]
  >()

  useEffect(() => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const policiesRef = collection(db, 'tenants', tenantId, 'policies')

      const unsubscribe = onSnapshot(policiesRef, (snapShot) => {
        const policies = snapShot.docs
          .filter((doc) => doc.data().customfile !== undefined && doc.data().status === 'Approved')
          .map((doc) => ({
            id: doc.id,
            title: doc.data().policy as string,
            documentURL: doc.data()?.customfile[0].url as string,
          }))

        const policyIdlist = addedPolicies
          .map((policy) => policy.policyId)
          .filter((policyId) => policyId !== '')

        setDefaultPolicies(policies.filter((policy) => !policyIdlist.includes(policy.id)))
      })

      return unsubscribe
    } catch (error) {
      console.error('Errow while getting default policies for trust center', error)
    }
  }, [addedPolicies])

  const onCancel = () => {
    setIsDrawerOpen(false)
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }

  const handleRemovePolicy = async () => {
    setLoading(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const policiesRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'policies',
        'all_policies'
      )

      if (toDeletePolicy?.isCustomDocumentUploaded) {
        const fileRef = ref(
          storage,
          `tenants/${currentUser?.tenantId}/trust_center/policies/${toDeletePolicy.documentId}-${toDeletePolicy?.documentName}`
        )

        await deleteObject(fileRef)
      }

      await deleteDoc(doc(policiesRef, toDeletePolicy?.id))

      const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = month + '/' + day + '/' + year

      await addDoc(activitiesRef, {
        message: `${toDeletePolicy?.title} policy for trust center has been removed`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        date: todaysDate,
        email: currentUser?.email,
        target: 'Trust Center',
        action: 'Policy removed',
      })

      showSuccessToast({message: 'Policy has been removed successfully'})
    } catch (error) {
      console.error('Error while removing policy: ', error)
    } finally {
      setLoading(false)
      setToDeletePolicy(undefined)
      setIsDeleteModalOpen(false)
    }
  }

  return (
    <div>
      <CustomDrawer isDrawerOpen={isDrawerOpen} onCancel={onCancel} title='Policies'>
        <div style={{padding: '20px 20px 80px'}}>
          <p className='text-dark mb-6' style={{fontSize: '13px'}}>
            Add all policy documents you would like to display in your Trust Center. You can assign
            restricted or public access to each documents.
          </p>

          <div className='d-flex flex-column'>
            <div className='d-flex justify-content-between align-items-center border-bottom border-gray-300 pb-1 mb-5'>
              <span className='text-dark fs-6 fw-bold'>Policies</span>

              <Button
                btnType='transparent'
                onClick={() => {
                  setToEditPolicy({
                    id: '',
                    access: '',
                    documentName: '',
                    documentURL: '',
                    title: '',
                  })
                  setIsModalOpen(true)
                }}
              >
                <img
                  alt='plus-icon'
                  src={toAbsoluteUrl('/media/icon/plus.svg')}
                  className='me-2'
                  style={{height: '18px', width: '18px'}}
                />
                <span>Add Policy</span>
              </Button>
            </div>

            {addedPolicies.length > 0 ? (
              addedPolicies.map((policy, index) => (
                <div
                  key={policy.id}
                  className='d-flex align-items-center'
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    gap: '12px',
                    padding: '20px',
                    marginBottom: '12px',
                  }}
                >
                  <div className='flex-fill d-flex align-items-center' style={{gap: '6px'}}>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{
                        backgroundColor: '#F3F4F6',
                        borderRadius: '12px',
                        height: '48px',
                        width: '48px',
                      }}
                    >
                      <img
                        alt='file-text-icon'
                        src={toAbsoluteUrl('/media/icon/file-text.svg')}
                        className=''
                        style={{height: '32px', width: '32px'}}
                      />
                    </div>

                    <h4 className='text-dark fs-6 fw-medium mb-0'>{policy.title}</h4>
                  </div>

                  <div
                    className='d-flex align-items-center'
                    style={{
                      backgroundColor: '#F3F4F6',
                      border: '#1px solid #E5E7EB',
                      borderRadius: '6px',
                      gap: '4px',
                      padding: '2px 8px',
                    }}
                  >
                    <img
                      alt={`${policy.access}-logo`}
                      src={`${
                        policy.access === 'Restricted'
                          ? toAbsoluteUrl('/media/icon/lock.svg')
                          : toAbsoluteUrl('/media/icon/world.svg')
                      }`}
                      className=''
                      style={{height: '14px', width: '14px'}}
                    />
                    <span
                      className='fw-medium text-center fs-7'
                      style={{
                        color: '#1F2A37',
                      }}
                    >
                      {policy.access}
                    </span>
                  </div>

                  <div className='d-flex' style={{gap: '8px'}}>
                    <Button
                      style={{
                        width: '34px',
                        height: '34px',
                      }}
                      onClick={() => {
                        setToEditPolicy(policy)
                        setIsModalOpen(true)
                      }}
                    >
                      <img
                        alt='edit-icon'
                        src={toAbsoluteUrl('/media/icon/edit.svg')}
                        className=''
                        style={{height: '18px', width: '18px'}}
                      />
                    </Button>

                    <Button
                      style={{
                        width: '34px',
                        height: '34px',
                      }}
                      onClick={() => {
                        setToDeletePolicy(policy)
                        setIsDeleteModalOpen(true)
                      }}
                    >
                      <img
                        alt='trash-icon'
                        src={toAbsoluteUrl('/media/icon/trash.svg')}
                        className=''
                        style={{height: '18px', width: '18px'}}
                      />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div
                className='w-100 d-flex justify-content-center align-items-center'
                style={{height: '245px'}}
              >
                <div className='d-flex flex-column align-items-center gap-4'>
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#F3F4F6',
                      borderRadius: '12px',
                      height: '48px',
                      width: '48px',
                    }}
                  >
                    <img
                      alt='file-text-icon'
                      src={toAbsoluteUrl('/media/icon/file-text.svg')}
                      className=''
                      style={{height: '32px', width: '32px'}}
                    />
                  </div>

                  <span className='text-dark fs-6 fw-medium'>No policies added</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CustomDrawer>

      {isModalOpen && (
        <AddEditPolicyModal
          isModalOpen={isModalOpen}
          hideModal={() => setIsModalOpen(false)}
          isEditMode={Boolean(toEditPolicy.id)}
          policy={toEditPolicy}
          availablePolicies={defaultPolicies!}
        />
      )}

      <Card
        title='Policies'
        actionComponent={
          <EditButton
            onClickHandler={() => {
              setIsDrawerOpen(true)
              const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
              document.body.style.overflow = 'hidden'
              document.body.style.paddingRight = `${scrollBarWidth}px`
            }}
          />
        }
      >
        {addedPolicies.length > 0 ? (
          <div className='row gy-5 gx-10 me-0'>
            {addedPolicies.map((policy, index) => (
              <div key={index} className='col-12 col-md-6 pe-0'>
                <div
                  className='d-flex align-items-center'
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    gap: '6px',
                    padding: '20px',
                  }}
                >
                  <div className='flex-fill d-flex align-items-center' style={{gap: '12px'}}>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{
                        backgroundColor: '#F3F4F6',
                        borderRadius: '12px',
                        height: '48px',
                        width: '48px',
                      }}
                    >
                      <img
                        alt='file-text-icon'
                        src={toAbsoluteUrl('/media/icon/file-text.svg')}
                        className=''
                        style={{height: '32px', width: '32px'}}
                      />
                    </div>

                    <span className='fw-medium text-dark fs-6'>{policy.title}</span>
                  </div>

                  <div
                    className='d-flex align-items-center'
                    style={{
                      backgroundColor: '#F3F4F6',
                      border: '#1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '2px 8px',
                      gap: '4px',
                    }}
                  >
                    <img
                      alt={`${policy.access}-logo`}
                      src={`${
                        policy.access === 'Restricted'
                          ? toAbsoluteUrl('/media/icon/lock.svg')
                          : toAbsoluteUrl('/media/icon/world.svg')
                      }`}
                      className=''
                      style={{height: '14px', width: '14px'}}
                    />
                    <span
                      className='fw-medium text-center fs-7'
                      style={{
                        color: '#1F2A37',
                      }}
                    >
                      {policy.access}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className='w-100 d-flex justify-content-center align-items-center'
            style={{height: '350px'}}
          >
            <div className='d-flex flex-column align-items-center gap-4'>
              <div
                className='d-flex align-items-center justify-content-center'
                style={{
                  backgroundColor: '#F3F4F6',
                  borderRadius: '12px',
                  height: '48px',
                  width: '48px',
                }}
              >
                <img
                  alt='file-text-icon'
                  src={toAbsoluteUrl('/media/icon/file-text.svg')}
                  className=''
                  style={{height: '32px', width: '32px'}}
                />
              </div>

              <span className='text-dark fs-6 fw-medium'>No policies added</span>
            </div>
          </div>
        )}
      </Card>

      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => setIsDeleteModalOpen(false)}
          confirmBtnClickHandler={handleRemovePolicy}
          toDeleteItemTitle='policy'
          isLoading={loading}
        />
      )}

      <ToastContainer />
    </div>
  )
}

export default Policies
